// Group — first-class scope of "who can see what".
// Matches DOCS/logic/identity/groups.md (Phase 1A).
import { z } from 'zod';
import { ProfileAvatarDesign } from '../members/avatar.js';
import { GroupRole } from './membership.js';
import { GroupVisibility } from './visibility.js';
// ---------------------------------------------------------------------------
// Group — canonical shape.
// ---------------------------------------------------------------------------
export const Group = z.object({
    id: z.string().uuid(),
    /** URL-safe slug, unique. */
    slug: z.string().regex(/^[a-z0-9](-?[a-z0-9])*$/).min(1).max(64),
    name: z.string().min(1).max(120),
    description: z.string(),
    /** Hex color for badges / banners (token-mappable). */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    /** Icon name from the doc-ui icon library. */
    icon: z.string().min(1).max(64),
    visibility: GroupVisibility,
    /** Optional welcome note shown to new members. */
    welcomeNote: z.string().nullable(),
    /**
     * Parent group id — feature-flag-gated UI but schema present from day 1.
     * Members of a child group automatically appear as members of the parent
     * for canRead purposes. See DOCS/logic/identity/groups.md §3.
     */
    parentGroupId: z.string().uuid().nullable(),
    /** Tags — flag-gated facet in the directory. */
    tags: z.array(z.string()),
    /** When set, group is archived (read-only). */
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    /** Viewer-relative annotation. The list / detail endpoints populate
     *  this with the requesting user's group_role (`'owner'`,
     *  `'moderator'`, `'member'`) or `null` for non-members + guests.
     *  Lets the frontend render Join / Apply / Switch affordances without
     *  a separate membership round-trip. Always present + nullable — use
     *  `null` for "no viewer" contexts (admin tooling, activity events). */
    viewerRole: z
        .enum(['member', 'moderator', 'owner'])
        .nullable(),
});
// ---------------------------------------------------------------------------
// House rule — simple text row.
// ---------------------------------------------------------------------------
export const GroupRule = z.object({
    groupId: z.string().uuid(),
    /** Display order, ascending. */
    position: z.number().int().min(0),
    text: z.string().min(1).max(280),
});
// ---------------------------------------------------------------------------
// GroupInvite — public tokenised-link row.
// ---------------------------------------------------------------------------
export const GroupInvite = z.object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    /** Optional recipient binding for notification-driven member invites. */
    targetUserId: z.string().uuid().nullable().optional(),
    /** Role granted on accept. */
    targetRole: GroupRole,
    expiresAt: z.string().datetime({ offset: true }),
    /** Optional access cap; null = unlimited. */
    maxUses: z.number().int().min(1).nullable(),
    usedCount: z.number().int().min(0),
    issuedBy: z.string().uuid(),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const GroupInviteEmailRule = z.object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    emailSuffix: z.string().min(3).max(255),
    targetRole: GroupRole,
    enabled: z.boolean(),
    createdBy: z.string().uuid(),
    lastRunAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const GroupFileUploadMode = z.enum([
    'admins-and-selected',
    'all-members',
    'admins-only',
]);
export const GroupFileSettings = z.object({
    groupId: z.string().uuid(),
    uploadMode: GroupFileUploadMode,
    uploaderUserIds: z.array(z.string().uuid()),
    canUploadFiles: z.boolean(),
    canManageFileSettings: z.boolean(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
});
export const UpdateGroupFileSettingsRequest = z.object({
    uploadMode: GroupFileUploadMode,
    uploaderUserIds: z.array(z.string().uuid()).max(500),
});
// ---------------------------------------------------------------------------
// GroupApplication — for visibility = 'request'.
// ---------------------------------------------------------------------------
export const GroupApplication = z.object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    userId: z.string().uuid(),
    displayName: z.string().optional(),
    avatarDesign: ProfileAvatarDesign.optional(),
    message: z.string().nullable(),
    status: z.enum(['pending', 'approved', 'denied', 'cancelled']),
    decidedBy: z.string().uuid().nullable(),
    decidedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// GroupMemberCandidate — moderator-only search result for targeted invites.
// ---------------------------------------------------------------------------
export const GroupMemberCandidate = z.object({
    id: z.string().uuid(),
    displayName: z.string().min(1),
    email: z.string().email().nullable(),
    avatarUrl: z.string().nullable(),
    avatarDesign: ProfileAvatarDesign.optional(),
});
//# sourceMappingURL=group.js.map